import React, { useState } from 'react';
import styled from 'styled-components';
import FormInput from './FormInput';

interface PasswordInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  fullWidth?: boolean;
}

const EyeIcon = ({ visible }: { visible: boolean }) => (
  visible ? (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M12 7.66992C14.3941 7.66996 16.33 9.60585 16.3301 12C16.33 14.3942 14.3942 16.33 12 16.3301C9.60581 16.3301 7.66996 14.3942 7.66992 12C7.66997 9.60583 9.60582 7.66992 12 7.66992ZM12 9.16992C10.4342 9.16992 9.16997 10.4343 9.16992 12C9.16996 13.5658 10.4342 14.8301 12 14.8301C13.5657 14.83 14.83 13.5657 14.8301 12C14.83 10.4343 13.5657 9.16996 12 9.16992Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M12 2.96973C15.8475 2.96979 19.3503 5.23846 21.7412 8.99609C22.2866 9.8505 22.5351 10.946 22.5352 11.9951C22.5351 13.0441 22.2874 14.1388 21.7422 14.9932L21.7412 14.9922C19.3503 18.7504 15.8479 21.0195 12 21.0195C8.15185 21.0195 4.64776 18.7508 2.25684 14.9922C1.71239 14.1381 1.46491 13.0434 1.46484 11.9951C1.46489 10.946 1.71245 9.8505 2.25781 8.99609C4.64872 5.23823 8.15231 2.96973 12 2.96973ZM12 4.46973C8.78821 4.46973 5.71153 6.36152 3.52246 9.80273C3.16797 10.3582 2.96489 11.1545 2.96484 11.9951C2.9649 12.7305 3.12031 13.4311 3.39648 13.9668L3.52246 14.1865C5.71153 17.6278 8.78819 19.5195 12 19.5195C15.2117 19.5195 18.2875 17.6285 20.4766 14.1875L20.4775 14.1865C20.832 13.6311 21.0351 12.8356 21.0352 11.9951C21.0351 11.1546 20.832 10.3592 20.4775 9.80371L20.4766 9.80273C18.2875 6.36157 15.2117 4.46979 12 4.46973Z" fill="currentColor" />
    </svg>
  ) : (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M12 7.66992C14.3941 7.66996 16.33 9.60585 16.3301 12C16.33 14.3942 14.3942 16.33 12 16.3301C9.60581 16.3301 7.66996 14.3942 7.66992 12C7.66997 9.60583 9.60582 7.66992 12 7.66992ZM12 9.16992C10.4342 9.16992 9.16997 10.4343 9.16992 12C9.16996 13.5658 10.4342 14.8301 12 14.8301C13.5657 14.83 14.83 13.5657 14.8301 12C14.83 10.4343 13.5657 9.16996 12 9.16992Z" fill="currentColor" opacity="0.5" />
      <path fillRule="evenodd" clipRule="evenodd" d="M12 2.96973C15.8475 2.96979 19.3503 5.23846 21.7412 8.99609C22.2866 9.8505 22.5351 10.946 22.5352 11.9951C22.5351 13.0441 22.2874 14.1388 21.7422 14.9932L21.7412 14.9922C19.3503 18.7504 15.8479 21.0195 12 21.0195C8.15185 21.0195 4.64776 18.7508 2.25684 14.9922C1.71239 14.1381 1.46491 13.0434 1.46484 11.9951C1.46489 10.946 1.71245 9.8505 2.25781 8.99609C4.64872 5.23823 8.15231 2.96973 12 2.96973ZM12 4.46973C8.78821 4.46973 5.71153 6.36152 3.52246 9.80273C3.16797 10.3582 2.96489 11.1545 2.96484 11.9951C2.9649 12.7305 3.12031 13.4311 3.39648 13.9668L3.52246 14.1865C5.71153 17.6278 8.78819 19.5195 12 19.5195C15.2117 19.5195 18.2875 17.6285 20.4766 14.1875L20.4775 14.1865C20.832 13.6311 21.0351 12.8356 21.0352 11.9951C21.0351 11.1546 20.832 10.3592 20.4775 9.80371L20.4766 9.80273C18.2875 6.36157 15.2117 4.46979 12 4.46973Z" fill="currentColor" opacity="0.5" />
      <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
);

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  placeholder,
  label,
  error,
  fullWidth = true,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <FormInput
      type={showPassword ? "text" : "password"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      label={label}
      error={error}
      fullWidth={fullWidth}
      icon={<EyeIcon visible={showPassword} />}
      onIconClick={toggleShowPassword}
    />
  );
};

export default PasswordInput; 